// The workspace rail: a vertical strip of icon+label buttons that switch one
// exclusive panel beside them.
//
// The distinction this module exists to enforce: the rail holds WORKSPACES, not
// layers. Layers are additive, multi-select and persistent; workspaces are
// exclusive. Mixing them makes a click unpredictable — so layer toggles live
// *inside* the Observations workspace, never in the rail itself. Time controls
// (play, scrub, now, pin) are likewise not here: the vertical rail is space and
// mode, the horizontal bar is time.
//
// Icon + label at 74 px, never icon-only. The console is read on a moving vessel
// with a trackpad and a weekly-at-best user; label-less icons cost the operator
// far more than the 74 px costs the planner. Same reason for the 30 px minimum
// hit target and the number-key shortcuts (1..9 select workspaces in order).
//
// A rail item may also carry a BADGE. This turns navigation into a status
// column: provisional operating limits, a stale in-theater station, a tropical
// system inside the current theater. Those are safety facts that must be visible
// without opening the panel they live in.

export type BadgeLevel = "stop" | "warn";

export interface Badge {
  level: BadgeLevel;
  /** Tooltip text — a badge with no explanation is decoration. */
  title: string;
}

export type IconName =
  | "theater"
  | "site"
  | "fleet"
  | "observations"
  | "svp"
  | "seep"
  | "ebs"
  | "tropical"
  | "systems"
  | "methods"
  | "feedback"
  | "theme"
  | "account";

export interface RailItem {
  id: string;
  label: string;
  icon: IconName;
  /** "workspace" (default) switches the panel; "action" just fires `onAction`. */
  kind?: "workspace" | "action";
  /** Panel heading, when it should differ from the rail label. */
  title?: string;
  /** Pushed to the bottom group, under a divider. Methods · Theme · Account. */
  bottom?: boolean;
  onAction?: () => void;
}

export interface RailOptions {
  items: RailItem[];
  /** Workspace shown first. Defaults to the first workspace item. */
  initial?: string;
  /**
   * Fired after the panel switches, with the newly selected workspace id.
   *
   * NOT fired for the initial selection. The caller already knows what that is
   * (`initial`, or the first workspace), and firing it during construction would
   * run the handler before `const rail = createRail(...)` is bound — so any
   * handler touching `rail` would hit the temporal dead zone and throw.
   */
  onSelect?: (id: string) => void;
  /**
   * Fired when the panel opens or closes. Clicking the workspace you are already
   * in closes the panel — the console is map-dominant, and 300 px of panel is a
   * real cost to pay while you are reading the field rather than the numbers.
   * The host uses this to give the space back (and to resize the map).
   */
  onPanelToggle?: (open: boolean) => void;
}

export interface Rail {
  /** The <nav> strip. Mount it as the first child of the app body. */
  rail: HTMLElement;
  /** The panel beside it. Mount it wherever the layout wants the panel. */
  panel: HTMLElement;
  /** The content container for one workspace — build sections into this. */
  workspace(id: string): HTMLElement;
  select(id: string): void;
  selected(): string | null;
  /** Panel open state. Closing keeps the selection, so re-opening returns to it. */
  setPanelOpen(open: boolean): void;
  panelOpen(): boolean;
  setBadge(id: string, badge: Badge | null): void;
  destroy(): void;
}

// --- icons ------------------------------------------------------------------
// Stroke-only, 24-unit box, currentColor. Deliberately plain: these are read at
// 21 px in motion, so silhouette beats detail.

const PATHS: Record<IconName, string[]> = {
  // a framed area with a centre mark — the theater box
  theater: ["M3 5h18v14H3z", "M12 10.5v3", "M10.5 12h3"],
  // crosshair over a point — the site
  site: ["M12 3v4", "M12 17v4", "M3 12h4", "M17 12h4", "M12 9.5a2.5 2.5 0 100 5 2.5 2.5 0 100-5"],
  // a hull on the water
  fleet: ["M3 17l1.6 3h14.8l1.6-3z", "M5.5 17V9h13l-2.5 8", "M12 9V5"],
  // stacked layers
  observations: ["M12 3l9 4.5-9 4.5-9-4.5z", "M3 12.5l9 4.5 9-4.5", "M3 17l9 4.5 9-4.5"],
  // a depth axis with a leaning sound-speed curve — the SVP profile
  svp: ["M6 4v16", "M6 4h12", "M16 6c-6 3-4 8-9 12"],
  // bubbles rising off the seabed — a gas flare over a seep
  seep: ["M3 20h18", "M8 20c1-3 3-3 4-6", "M12 11a1.6 1.6 0 100-3.2 1.6 1.6 0 100 3.2", "M15 6.5a1.2 1.2 0 100-2.4 1.2 1.2 0 100 2.4"],
  // a rosette bottle on a wire, over the sample depth — the EBS cast
  ebs: ["M12 3v4", "M8.5 7h7v7a3.5 3.5 0 01-7 0z", "M4 19.5c2.5-1.5 5-1.5 8 0s5.5 1.5 8 0"],
  // a cyclone
  tropical: [
    "M12 12a4 4 0 014-4 6 6 0 01-9 8",
    "M12 12a4 4 0 01-4 4 6 6 0 019-8",
  ],
  // a pulse trace — source health at a glance
  systems: ["M3 12h4l2-5 3 10 2.5-7 1.5 2h5"],
  // a speech bubble with a tail — say something back
  feedback: ["M4 5.5h16v10H9l-4 3.5v-3.5H4z"],
  // an open book
  methods: ["M12 6.5v13", "M12 6.5C10 5 7 4.5 3 5v13c4-.5 7 0 9 1.5", "M12 6.5C14 5 17 4.5 21 5v13c-4-.5-7 0-9 1.5"],
  // half-lit disc — day/dusk/night
  theme: ["M12 3a9 9 0 100 18 9 9 0 100-18", "M12 3v18", "M12 5.5a6.5 6.5 0 010 13"],
  account: ["M12 4a3.5 3.5 0 100 7 3.5 3.5 0 100-7", "M4.5 20a7.5 7.5 0 0115 0"],
};

const SVG_NS = "http://www.w3.org/2000/svg";

function icon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  for (const d of PATHS[name]) {
    const p = document.createElementNS(SVG_NS, "path");
    p.setAttribute("d", d);
    svg.appendChild(p);
  }
  return svg;
}

// --- sections ---------------------------------------------------------------

export interface SectionOptions {
  open?: boolean;
  /** Stable id, so the ⓘ buttons on the tracks can deep-link to a description. */
  id?: string;
}

/**
 * A collapsible section inside a workspace. Returns the body element to fill.
 *
 * <details> rather than a bespoke disclosure: it collapses without script, is
 * keyboard- and screen-reader-correct for free, and survives the panel being
 * rebuilt on a point load.
 */
export function railSection(
  parent: HTMLElement,
  title: string,
  opts: SectionOptions = {},
): HTMLElement {
  const d = document.createElement("details");
  d.className = "rsec";
  if (opts.open === true) d.open = true;
  if (opts.id !== undefined) d.id = opts.id;
  const s = document.createElement("summary");
  s.textContent = title;
  const body = document.createElement("div");
  body.className = "rsbody";
  d.append(s, body);
  parent.appendChild(d);
  return body;
}

/**
 * Open a section by id. Select its workspace FIRST — a hidden workspace has no
 * layout, so the scroll below silently does nothing.
 */
export function openSection(id: string): void {
  const d = document.getElementById(id);
  if (d instanceof HTMLDetailsElement) {
    d.open = true;
    d.scrollIntoView({ block: "nearest" });
  }
}

// --- the rail ---------------------------------------------------------------

interface Entry {
  item: RailItem;
  btn: HTMLButtonElement;
  ws: HTMLElement | null;
  badge: HTMLSpanElement | null;
}

/** True when a keystroke belongs to whatever the user is typing in. */
function typingInto(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function createRail(opts: RailOptions): Rail {
  const rail = document.createElement("nav");
  rail.className = "rail";
  rail.setAttribute("aria-label", "Workspaces");

  const panel = document.createElement("aside");
  panel.className = "wspanel";
  const heading = document.createElement("h4");
  heading.className = "wshead";
  heading.id = "wspanel-head";
  panel.setAttribute("aria-labelledby", heading.id);
  const body = document.createElement("div");
  body.className = "wsbody";
  panel.append(heading, body);

  const entries = new Map<string, Entry>();
  const order: string[] = []; // workspace ids, in rail order — the 1..9 shortcuts
  let current: string | null = null;
  let bottomStarted = false;

  const build = (item: RailItem): void => {
    if (item.bottom === true && !bottomStarted) {
      bottomStarted = true;
      rail.appendChild(document.createElement("hr"));
    }
    const b = document.createElement("button");
    b.type = "button";
    b.className = "rbtn";
    b.appendChild(icon(item.icon));
    const span = document.createElement("span");
    span.textContent = item.label;
    b.appendChild(span);

    const isWorkspace = (item.kind ?? "workspace") === "workspace";
    let ws: HTMLElement | null = null;
    if (isWorkspace) {
      b.setAttribute("aria-pressed", "false");
      const n = order.length + 1;
      if (n <= 9) b.title = `${item.title ?? item.label} (${n}) — click again to close the panel`;
      ws = document.createElement("div");
      ws.className = "ws";
      ws.hidden = true;
      body.appendChild(ws);
      order.push(item.id);
      b.addEventListener("click", () => select(item.id));
    } else {
      if (item.title !== undefined) b.title = item.title;
      b.addEventListener("click", () => item.onAction?.());
    }
    rail.appendChild(b);
    entries.set(item.id, { item, btn: b, ws, badge: null });
  };

  // The spring goes in before the first bottom item, pushing that group down.
  const tops = opts.items.filter((i) => i.bottom !== true);
  const bottoms = opts.items.filter((i) => i.bottom === true);
  for (const i of tops) build(i);
  const spring = document.createElement("div");
  spring.className = "rspring";
  rail.appendChild(spring);
  for (const i of bottoms) build(i);

  let announcing = false; // false only while the constructor sets the first workspace
  let open = true;

  function setPanelOpen(next: boolean): void {
    if (next === open) return;
    open = next;
    panel.hidden = !next;
    // A closed panel means no workspace is showing, so nothing is pressed. The
    // rail must not claim a state the screen isn't in.
    const cur = current;
    if (cur !== null) entries.get(cur)?.btn.setAttribute("aria-pressed", next ? "true" : "false");
    opts.onPanelToggle?.(next);
  }

  function select(id: string): void {
    const e = entries.get(id);
    if (e === undefined || e.ws === null) return;
    if (id === current) {
      setPanelOpen(!open); // clicking the workspace you are in toggles the panel
      return;
    }
    if (!open) setPanelOpen(true);
    for (const [k, other] of entries) {
      if (other.ws === null) continue;
      const on = k === id;
      other.ws.hidden = !on;
      other.btn.setAttribute("aria-pressed", on ? "true" : "false");
    }
    current = id;
    heading.textContent = e.item.title ?? e.item.label;
    if (announcing) opts.onSelect?.(id);
  }

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.altKey || ev.ctrlKey || ev.metaKey || typingInto(ev.target)) return;
    const n = Number(ev.key);
    if (!Number.isInteger(n) || n < 1 || n > order.length) return;
    ev.preventDefault();
    const id = order[n - 1];
    if (id !== undefined) select(id);
  };
  document.addEventListener("keydown", onKey);

  const first = opts.initial ?? order[0];
  if (first !== undefined) select(first);
  announcing = true;

  return {
    rail,
    panel,
    workspace(id) {
      const e = entries.get(id);
      if (e === undefined || e.ws === null) throw new Error(`rail: no workspace "${id}"`);
      return e.ws;
    },
    select,
    selected: () => current,
    setPanelOpen,
    panelOpen: () => open,
    setBadge(id, badge) {
      const e = entries.get(id);
      if (e === undefined) return;
      if (badge === null) {
        e.badge?.remove();
        e.badge = null;
        e.btn.removeAttribute("data-badge");
        return;
      }
      if (e.badge === null) {
        e.badge = document.createElement("span");
        e.badge.className = "rbadge";
        e.btn.appendChild(e.badge);
      }
      e.badge.className = `rbadge ${badge.level}`;
      // Screen readers get the reason; sighted users get it on hover. The dot
      // alone would be an unexplained mark.
      e.badge.setAttribute("role", "img");
      e.badge.setAttribute("aria-label", badge.title);
      e.badge.title = badge.title;
      e.btn.setAttribute("data-badge", badge.level);
    },
    destroy() {
      document.removeEventListener("keydown", onKey);
      rail.remove();
      panel.remove();
      entries.clear();
    },
  };
}
